import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Loader2, Plus, Trash2, ArrowUp, ArrowDown, Wand2, ImageOff, PackageSearch,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { cssUrl } from '@/lib/cssUrl';

/**
 * Admin · Marketplace Shelves — the aisles shoppers browse.
 *
 * Vendors keep typing their own category words (their product lines). Those stay on their
 * storefront as Collections. What the marketplace grid shows is these shelves, and only an
 * admin creates them: name, artwork, the keywords that file products automatically, and order.
 */
export default function AdminShelvesTab() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [remapping, setRemapping] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const [unshelved, setUnshelved] = useState(null);
  const [selected, setSelected] = useState([]);
  const [assignTo, setAssignTo] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/marketplace/shelves`, { headers });
      setShelves(res.data.shelves || []);
    } catch (err) {
      toast.error('Could not load the shelves');
    } finally { setLoading(false); }
  }, []);

  const loadUnshelved = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/marketplace/shelves/unshelved?per_page=100`, { headers });
      setUnshelved(res.data);
    } catch (err) { toast.error('Could not load unshelved products'); }
  }, []);

  useEffect(() => { load(); loadUnshelved(); }, [load, loadUnshelved]);

  const patchLocal = (id, changes) =>
    setShelves((prev) => prev.map((s) => (s.id === id ? { ...s, ...changes } : s)));

  const save = async (shelf) => {
    setSavingId(shelf.id);
    try {
      await axios.patch(`${API}/admin/marketplace/shelves/${shelf.id}`, {
        name: shelf.name,
        artwork_url: shelf.artwork_url || '',
        intro: shelf.intro || '',
        keywords: (shelf.keywordsText !== undefined
          ? shelf.keywordsText.split(',')
          : shelf.keywords || []).map((k) => k.trim()).filter(Boolean),
        active: !!shelf.active,
      }, { headers });
      toast.success(`${shelf.name} saved`);
      load();
    } catch (err) { toast.error('Save failed'); }
    finally { setSavingId(null); }
  };

  const create = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/marketplace/shelves`, { name: newName.trim() }, { headers });
      setNewName('');
      toast.success('Shelf added');
      load();
    } catch (err) { toast.error('Could not add that shelf'); }
    finally { setCreating(false); }
  };

  const remove = async (shelf) => {
    if (!window.confirm(`Delete "${shelf.name}"? Products on it stay listed, just unshelved.`)) return;
    try {
      await axios.delete(`${API}/admin/marketplace/shelves/${shelf.id}`, { headers });
      toast.success('Shelf deleted');
      load(); loadUnshelved();
    } catch (err) { toast.error('Delete failed'); }
  };

  const move = async (index, dir) => {
    const next = [...shelves];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setShelves(next);
    try {
      await axios.put(`${API}/admin/marketplace/shelves/order`,
        { shelf_ids: next.map((s) => s.id) }, { headers });
    } catch (err) { toast.error('Could not save the new order'); load(); }
  };

  const remap = async (useAi) => {
    setRemapping(true);
    try {
      const res = await axios.post(`${API}/admin/marketplace/shelves/backfill`,
        { use_ai: useAi, only_unshelved: true }, { headers });
      const d = res.data;
      toast.success(`${d.by_keyword + d.by_ai} products shelved · ${d.unshelved} still unshelved`);
      load(); loadUnshelved();
    } catch (err) { toast.error('Re-map failed'); }
    finally { setRemapping(false); }
  };

  const bulkAssign = async () => {
    if (!selected.length || !assignTo.length) return;
    try {
      await axios.post(`${API}/admin/marketplace/shelves/assign`,
        { product_ids: selected, shelf_ids: assignTo }, { headers });
      toast.success(`${selected.length} product(s) shelved`);
      setSelected([]); setAssignTo([]);
      load(); loadUnshelved();
    } catch (err) { toast.error('Could not assign those'); }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-shelves-tab">
      <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3">
        <p className="text-sm text-gray-300">
          Shelves are the aisles of your marketplace. A vendor's own category word stays theirs —
          it becomes a collection on their storefront — and never appears here.
        </p>
        <div className="flex gap-2 flex-wrap items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-gray-400">New shelf</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Ritual Kits" data-testid="shelf-new-name"
              className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500" />
          </div>
          <Button onClick={create} disabled={creating || !newName.trim()} data-testid="shelf-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Add shelf
          </Button>
          <Button variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => remap(true)} disabled={remapping}
            data-testid="shelf-remap-btn">
            {remapping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Shelve unshelved products
          </Button>
        </div>
      </div>

      {shelves.map((shelf, i) => (
        <div key={shelf.id} className="rounded-xl bg-white/5 border border-white/10 p-4"
          data-testid={`shelf-row-${shelf.slug}`}>
          <div className="flex gap-4 flex-wrap">
            <div className="w-28 h-20 rounded-lg border border-white/10 bg-black/30 bg-cover bg-center flex items-center justify-center shrink-0"
              style={shelf.artwork_url ? { backgroundImage: cssUrl(shelf.artwork_url) } : undefined}>
              {!shelf.artwork_url && <ImageOff className="w-5 h-5 text-gray-500" />}
            </div>

            <div className="flex-1 min-w-[260px] space-y-2">
              <div className="flex gap-2 items-center flex-wrap">
                <Input value={shelf.name} onChange={(e) => patchLocal(shelf.id, { name: e.target.value })}
                  className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500 max-w-xs" data-testid={`shelf-name-${shelf.slug}`} />
                <span className="text-xs text-gray-400">/{shelf.slug}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-black/40 text-gray-300">
                  {shelf.product_count} live
                </span>
                <div className="flex items-center gap-2 ml-auto">
                  <Switch checked={!!shelf.active}
                    onCheckedChange={(v) => patchLocal(shelf.id, { active: v })}
                    data-testid={`shelf-active-${shelf.slug}`} />
                  <span className="text-xs text-gray-400">{shelf.active ? 'Shown' : 'Hidden'}</span>
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-400">Artwork URL</Label>
                <Input value={shelf.artwork_url || ''}
                  onChange={(e) => patchLocal(shelf.id, { artwork_url: e.target.value })}
                  placeholder="https://…" className="bg-black/30 border-white/10 text-gray-100 placeholder:text-gray-500"
                  data-testid={`shelf-artwork-${shelf.slug}`} />
              </div>

              <div>
                <Label className="text-xs text-gray-400">Intro shown on the shelf's own page</Label>
                <textarea
                  value={shelf.intro || ''}
                  onChange={(e) => patchLocal(shelf.id, { intro: e.target.value })}
                  rows={2}
                  placeholder="A line or two under the artwork. Leave blank to show nothing."
                  className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm text-gray-100 placeholder:text-gray-500"
                  data-testid={`shelf-intro-${shelf.slug}`}
                />
              </div>

              <div>
                <Label className="text-xs text-gray-400">
                  Keywords that file a product here automatically (comma separated)
                </Label>
                <textarea
                  value={shelf.keywordsText !== undefined ? shelf.keywordsText : (shelf.keywords || []).join(', ')}
                  onChange={(e) => patchLocal(shelf.id, { keywordsText: e.target.value })}
                  rows={2}
                  className="w-full rounded-md bg-black/30 border border-white/10 p-2 text-sm text-gray-100 placeholder:text-gray-500"
                  data-testid={`shelf-keywords-${shelf.slug}`}
                />
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => save(shelf)} disabled={savingId === shelf.id}
                  data-testid={`shelf-save-${shelf.slug}`}>
                  {savingId === shelf.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </Button>
                <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => move(i, -1)} disabled={i === 0}
                  data-testid={`shelf-up-${shelf.slug}`}><ArrowUp className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" className="border-white/20 text-gray-100 hover:bg-white/10" onClick={() => move(i, 1)}
                  disabled={i === shelves.length - 1}
                  data-testid={`shelf-down-${shelf.slug}`}><ArrowDown className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-200"
                  onClick={() => remove(shelf)} data-testid={`shelf-delete-${shelf.slug}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Needs a shelf */}
      <div className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-3"
        data-testid="shelf-unshelved-block">
        <div className="flex items-center gap-2">
          <PackageSearch className="w-4 h-4 text-amber-300" />
          <p className="text-sm text-gray-200 font-medium">
            Needs a shelf {unshelved ? `(${unshelved.total})` : ''}
          </p>
        </div>
        <p className="text-xs text-gray-400">
          These are still listed and searchable, and they show under All — they just have no aisle.
        </p>

        {unshelved && unshelved.products.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2">
              {shelves.filter((s) => s.active).map((s) => (
                <button key={s.id} type="button"
                  onClick={() => setAssignTo((prev) => prev.includes(s.id)
                    ? prev.filter((x) => x !== s.id)
                    : (prev.length < 3 ? [...prev, s.id] : prev))}
                  data-testid={`shelf-assign-pick-${s.slug}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    assignTo.includes(s.id)
                      ? 'bg-teal-600 border-teal-500 text-white'
                      : 'border-white/15 text-gray-300 hover:border-teal-500'}`}>
                  {s.name}
                </button>
              ))}
              <Button size="sm" onClick={bulkAssign} disabled={!selected.length || !assignTo.length}
                data-testid="shelf-bulk-assign-btn">
                Put {selected.length || ''} on {assignTo.length || 0} shelf(s)
              </Button>
            </div>

            <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
              {unshelved.products.map((p) => (
                <label key={p.id} className="flex items-center gap-3 py-2 cursor-pointer"
                  data-testid={`unshelved-${p.id}`}>
                  <input type="checkbox" checked={selected.includes(p.id)}
                    onChange={() => setSelected((prev) => prev.includes(p.id)
                      ? prev.filter((x) => x !== p.id) : [...prev, p.id])} />
                  {p.images?.[0]
                    ? <img src={p.images[0]} alt="" className="w-8 h-8 rounded object-cover" />
                    : <span className="w-8 h-8 rounded bg-black/40" />}
                  <span className="text-sm text-gray-200 flex-1">{p.title}</span>
                  <span className="text-xs text-gray-500">{p.category || 'no category'}</span>
                  <span className="text-xs text-gray-500">{p.status}</span>
                </label>
              ))}
            </div>
          </>
        )}

        {unshelved && unshelved.products.length === 0 && (
          <p className="text-xs text-gray-400" data-testid="shelf-unshelved-empty">
            Everything has a shelf.
          </p>
        )}
      </div>
    </div>
  );
}
